import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FilterExpression, FilterModule, } from '@progress/kendo-angular-filter';
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import type { DialogAction } from '../../../services/dialog/dialog-action';
import type { DialogContext } from '../../../services/dialog/dialog-data';
import { DashboardDialogFramePart } from '../../../services/dialog/dialog-frame/dialog-frame.part';
import { GridColumn } from '../interfaces';


interface FilterDialogData<RowT extends { updatedUTC: number }> {
	filters: CompositeFilterDescriptor,
	columns: GridColumn<RowT, unknown>[],
}


@Component({
	selector: 'dashboard-grid-filter-dialog',
	imports: [DashboardDialogFramePart, FilterModule],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './filter.dialog.html'
})
export class GridFilterDialog<RowT extends { updatedUTC: number }> {

	public dialogRef = inject(MatDialogRef<GridFilterDialog<RowT>>);
	public dialogContext: DialogContext<FilterDialogData<RowT>> = inject(MAT_DIALOG_DATA);

	public readonly originalFilters = computed<CompositeFilterDescriptor>(() => this.dialogContext.data?.filters ?? { filters: Array(0), logic: 'and' });
	private readonly _updatedFilters = signal<CompositeFilterDescriptor | undefined>(undefined);
	public readonly updatedFilters = computed(() => this._updatedFilters() ?? this.originalFilters());


	public readonly filterExpressions = computed<FilterExpression[]>(() => {
		return this.mapColumnsToFilterExpressions(this.dialogContext.data?.columns ?? []);
	});


	private applyButtonCssClass = computed(() => this._updatedFilters() ? 'btn-green' : '');


	public actions = computed<DialogAction[]>(() => {
		return [
			{
				id: 'apply',
				cssClass: computed(() => this.applyButtonCssClass()),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Apply'),
				linkType: "callback",
				callback: async () => (this.updatedFilters()),
				willCloseDialog: true,
			},
			{
				id: 'clear',
				cssClass: signal('btn-danger'),
				enabled: signal(true),
				visible: signal(true),
				text: signal('Clear'),
				linkType: "no-action",
				willCloseDialog: true,
			}
		];
	});


	mapColumnsToFilterExpressions(columns: GridColumn<RowT, unknown>[]): FilterExpression[] {

		const expressions: FilterExpression[] = columns
			.map(column => {
				const e: FilterExpression = {
					field: column.flatRowField,
					title: column.header as string,
					editor: this.getFilterEditor(column),
				};

				return e;
			})
			.filter(e => !!e.editor);	// Unfilterable columns will have editor set to undefined

		return expressions;
	}


	/**
	 * This function overcomes inconsistencies with Kendo filter types. The KendoColumn used filter
	 * types of 'text' and 'numeric' but the FilterExpression interface uses 'string' and 'number'
	 */
	getFilterEditor(column: GridColumn<RowT, unknown>) {

		const filterType = column.filterType;

		if (filterType == 'numeric') return 'number';
		if (filterType == 'boolean') return 'boolean';
		if (filterType == 'date') return 'date';

		return 'string';
	}


	onFiltersChange(filters: CompositeFilterDescriptor | undefined) {
		if (filters) {
			this._updatedFilters.set(filters);
		}
	}

}